import * as React from "react";
import * as React44ms from "../../../Util/form-utils/react-44ms";
import * as ReactQuery from "react-query";
import { UseQueryResult } from "react-query";
import axios from "axios";

import { DataSourceAddressParse_QueryKey } from "./data-source-address-parse-query-key-factory";

export interface AddressParse_QueryResult {
  data?: {
    /**
     * Suburb
     */
    city?: string;
    /**
     * Country Code
     */
    country_id?: string;
    postcode?: string;
    /**
     * State
     */
    region?: string;
    street0?: string;
    street1?: string;
  };
}

export interface DataSourceAddressParseQueryConsumerInterface {
  data: UseQueryResult<AddressParse_QueryResult>["data"];
  status: UseQueryResult<AddressParse_QueryResult>["status"];

  originalRequest: DataSourceAddressParseQueryProviderProps;
}
const defaultValue =
  {} as unknown as DataSourceAddressParseQueryConsumerInterface;
const DataSourceAddressParseQueryContext = React.createContext(defaultValue);
const DataSourceAddressParseQueryContext_44 = React44ms.React.createContext(
  defaultValue,
  DataSourceAddressParseQueryContext
);

export interface DataSourceAddressParseQueryProviderProps {
  addressToParse?: string;
  countryCode?: string;
}
const addressFormatCache: { [key: string]: any } = {};

export const DataSourceAddressParseQueryProvider: React.FunctionComponent<
  DataSourceAddressParseQueryProviderProps
> = (props) => {
  const queryKey = React.useMemo(() => {
    return DataSourceAddressParse_QueryKey.addressParse(props);
  }, [props, props.addressToParse, props.countryCode]);

  const queryFn: ReactQuery.QueryFunction<
    AddressParse_QueryResult,
    typeof queryKey
  > = async (context: ReactQuery.QueryFunctionContext<typeof queryKey>) => {
    const [baseQuery, desiredAddress] = context.queryKey;

    const { addressToParse, countryCode } = desiredAddress;

    if ((addressToParse || "").length < 1 || (countryCode || "").length < 1) {
      return {};
    }

    let formattedAddress = {};

    if (addressToParse && !addressFormatCache[addressToParse]) {
      const { data: addressParse_queryResult } =
        await axios.get<AddressParse_QueryResult>(
          `/addressvalidation/address/format?address=${addressToParse}&country=${countryCode}`
        );
      formattedAddress = addressParse_queryResult;
      addressFormatCache[addressToParse] = addressParse_queryResult;
    } else if (addressToParse && addressFormatCache[addressToParse]) {
      formattedAddress = addressFormatCache[addressToParse];
    }

    return formattedAddress;
  };

  const { status, data, ...result } = ReactQuery.useQuery(queryKey, queryFn, {
    refetchOnWindowFocus: false,
  });

  const value: DataSourceAddressParseQueryConsumerInterface = React.useMemo(
    () => ({
      status,
      data,
      originalRequest: props,
    }),
    [status, data, props]
  );

  return (
    <DataSourceAddressParseQueryContext.Provider value={value}>
      <DataSourceAddressParseQueryContext_44.Provider value={value}>
        {props?.children || []}
      </DataSourceAddressParseQueryContext_44.Provider>
    </DataSourceAddressParseQueryContext.Provider>
  );
};

export const useDataSourceAddressParseQuery = () => {
  const context = React.useContext(DataSourceAddressParseQueryContext);
  /**
   * Normally we do this pattern below for using any provider,
   * this here is a special case where
   * we want the defaultValue to be used when this provider isn't in the tree,
   * because it can be used when there is a fully formed request information packet,
   * which isn't guaranteed to be available immediately.
   */
  // if (context === defaultValue) {
  //   throw new Error(
  //     "useDataSourceAddressParseQuery must be used within a DataSourceAddressParseQueryProvider"
  //   );
  // }
  return context;
};

export const useDataSourceAddressParseQuery_44 = () => {
  const context = React44ms.React.useContext(
    DataSourceAddressParseQueryContext_44
  );
  /**
   * Normally we do this pattern below for using any provider,
   * this here is a special case where
   * we want the defaultValue to be used when this provider isn't in the tree,
   * because it can be used when there is a fully formed request information packet,
   * which isn't guaranteed to be available immediately.
   */
  // if (context === defaultValue) {
  //   throw new Error(
  //     "useDataSourceAddressParseQuery must be used within a DataSourceAddressParseQueryProvider"
  //   );
  // }
  return context;
};
